/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from "react";

/**
 * Used to create a style hook to utilise JSS
 */
import { Theme, makeStyles } from "@material-ui/core/styles";

/**
 * Used to format text.
 */
import Typography from "@material-ui/core/Typography";

/**
 * Used to style buttons.
 */
import Button from "@material-ui/core/Button";

import {
    PatientContext,
    InstitutionContext,
    onlinePatientManagement,
} from "@ngt/online-patient-management-ui";

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to get the BCT Website URL.
 */

import { RequestState } from "@ngt/request-utilities";

import useInstitutionPermissions from "../hooks/useInstitutionPermissions";

import * as Dtos from "../api/Neon.Dtos";

import PatientSummaryList from "../components/PatientSummary/PatientSummaryList";
import PatientInformation from "../components/PatientInformation/PatientInformation";
import PatientBreadcrumbs from "../components/Breadcrumbs/PatientBreadcrumbs";
import PatientErrorContext from "../context/PatientErrorContext";
import { useSnackbar } from "notistack";
import {
    DialogTitle,
    DialogContent,
    Dialog,
    DialogContentText,
    DialogActions,
    TextField,
    Paper,
} from "@material-ui/core";
import { DateTime } from "luxon";
import PageLayout from "src/components/Layout/PageLayout";

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientSummaryProps {}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>((theme) => ({
    container: {
        padding: theme.spacing(3),
    },
    heading: {},
    headingContainer: {
        marginTop: theme.spacing(4),
        display: "flex",
        alignItems: "center",

        "& :nth-child(2)": {
            marginLeft: "auto",
        },
    },
    patientSummary: {
        marginTop: theme.spacing(3),
    },
    buttonSet: {
        textAlign: "right",
        paddingTop: theme.spacing(3),
    },
    button: {
        marginLeft: theme.spacing(3),

        "&:first-child": {
            marginLeft: theme.spacing(0),
        },
    },
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const opmsPermissions = [
    Dtos.Permission.OpmsAdminister,
    Dtos.Permission.OpmsPatientUpdate,
];

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const PatientSummary: React.FunctionComponent<IPatientSummaryProps> = () => {
    const { enqueueSnackbar } = useSnackbar();

    const classes = useStyles();

    const [modalOpen, setModalOpen] = React.useState(false);

    const [institution, , , ,] = React.useContext(InstitutionContext);

    const [storePatient, , patientSaveState, ,] =
        React.useContext(PatientContext);

    const [modifiedPatient, setModifiedPatient] = React.useState(storePatient);
    const [updating, setUpdating] = React.useState(false);

    const patient = React.useMemo(() => {
        return modifiedPatient ?? storePatient;
    }, [storePatient, modifiedPatient]);

    const [[isAdministrator, canUpdatePatient]] = useInstitutionPermissions(
        institution?.code ?? null,
        opmsPermissions
    );

    const [registrationModalOpen, setRegistrationModalOpen] =
        React.useState(false);

    const [ineligibleModalOpen, setIneligibleModalOpen] = React.useState(false);

    const [patientStatusUpdateReason, setPatientStatusUpdateReason] =
        React.useState("");

    const recruitmentDisabled = React.useMemo(() => {
        return (institution as Dtos.Institution)?.recruitmentDisabled ?? false;
    }, [institution]);

    const onCompleteScreeningClick = React.useCallback(() => {
        if (patient) {
            setUpdating(true);
            onlinePatientManagement.client
                .post(
                    new Dtos.CompleteScreening({
                        patientId: patient.id,
                    })
                )
                .then((response) => {
                    setUpdating(false);
                    setModifiedPatient(response.patient);

                    enqueueSnackbar(
                        "Screening has been successfully completed.",
                        { variant: "success" }
                    );
                })
                .catch((e) => {
                    setUpdating(false);

                    enqueueSnackbar(
                        "An error occurred while attempting to complete the screening.",
                        { variant: "error" }
                    );
                });
        }
    }, [setModifiedPatient, setUpdating, patient, enqueueSnackbar]);

    const onRegisterOkClick = React.useCallback(() => {
        setRegistrationModalOpen(false);
        setModalOpen(false);

        if (patient) {
            setUpdating(true);
            onlinePatientManagement.client
                .post(
                    new Dtos.RegisterPatient({
                        patientId: patient.id,
                    })
                )
                .then((response) => {
                    setUpdating(false);
                    setModifiedPatient(response.patient);

                    const typedPatient: Dtos.Patient =
                        response.patient as Dtos.Patient;

                    enqueueSnackbar(
                        `Patient has been successfully registered to treatment Arm ${
                            typedPatient.treatmentId === 1
                                ? "A"
                                : typedPatient.treatmentId === 2
                                ? "B"
                                : "C"
                        }.`,
                        { variant: "success" }
                    );
                })
                .catch((e) => {
                    setUpdating(false);

                    enqueueSnackbar(
                        "An error occurred while attempting to register the patient.",
                        { variant: "error" }
                    );
                });
        }
    }, [setModifiedPatient, setUpdating, patient, enqueueSnackbar]);

    const onRegisterClick = React.useCallback(() => {
        setModalOpen(true);
    }, [setModalOpen]);

    const onRegisterClose = React.useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);

    //const onRandomiseClick = React.useCallback(() => {
    //    setModalOpen(true);
    //}, [setModalOpen]);

    //const onRandomiseClose = React.useCallback(() => {
    //    setModalOpen(false);
    //}, [setModalOpen]);

    //const onRandomiseOkClick = React.useCallback(() => {
    //    setRegistrationModalOpen(false);
    //    setModalOpen(false);

    //    if (patient) {
    //        setUpdating(true);
    //        onlinePatientManagement.client
    //            .post(new Dtos.RandomisePatient({
    //                patientId: patient.id
    //            }))
    //            .then(response => {
    //                setUpdating(false);
    //                setModifiedPatient(response.patient);

    //                const typedPatient: Dtos.Patient = response.patient as Dtos.Patient;

    //                enqueueSnackbar(`Patient has been successfully randomised to treatment Arm ${(typedPatient.treatmentId === 1 ? 'A' : 'B')}.`, { variant: 'success' });
    //            })
    //            .catch((e) => {
    //                setUpdating(false);

    //                enqueueSnackbar('An error occurred while attempting to randomise the patient.', { variant: 'error' });
    //            })
    //    }
    //}, [setModifiedPatient, setUpdating, patient, setModalOpen, enqueueSnackbar]);

    const toggleIneligibleModal = React.useCallback(() => {
        setIneligibleModalOpen((state) => !state);
    }, [setIneligibleModalOpen]);

    const onIneligibleClick = React.useCallback(() => {
        setIneligibleModalOpen(false);
        if (patient) {
            setUpdating(true);
            onlinePatientManagement.client
                .put(
                    new Dtos.MarkAsIneligiblePatient({
                        id: patient.id,
                        status: Dtos.PatientStatuses.Ineligible,
                        statusReason: patientStatusUpdateReason,
                        ineligibleDate: DateTime.local().toISO({
                            includeOffset: false,
                        }),
                    })
                )
                .then((response) => {
                    setUpdating(false);
                    setModifiedPatient(response.patient);

                    enqueueSnackbar(
                        "Patient has been successfully marked as ineligible.",
                        { variant: "success" }
                    );
                    setPatientStatusUpdateReason("");
                })
                .catch((_e) => {
                    setUpdating(false);

                    enqueueSnackbar(
                        "An error occurred while attempting to mark the patient as ineligible.",
                        { variant: "error" }
                    );
                    setPatientStatusUpdateReason("");
                });
        }
    }, [
        setModifiedPatient,
        setUpdating,
        patient,
        setIneligibleModalOpen,
        patientStatusUpdateReason,
        setPatientStatusUpdateReason,
        enqueueSnackbar,
    ]);

    const onRestoreClick = React.useCallback(() => {
        if (patient) {
            setUpdating(true);
            onlinePatientManagement.client
                .put(
                    new Dtos.PatientPutUpdate({
                        id: patient.id,
                        patient: new Dtos.Patient({
                            ...patient,
                            status: Dtos.PatientStatuses.NewPatient,
                            statusReason: undefined,
                            ineligibleDate: undefined,
                        }),
                    })
                )
                .then((response) => {
                    setUpdating(false);
                    setModifiedPatient(response.patient);

                    enqueueSnackbar("Patient has been successfully restored.", {
                        variant: "success",
                    });
                })
                .catch((_e) => {
                    setUpdating(false);

                    enqueueSnackbar(
                        "An error occurred while attempting to restore the patient.",
                        { variant: "error" }
                    );
                });
        }
    }, [setModifiedPatient, setUpdating, patient, enqueueSnackbar]);

    const [errorResult, setErrorResult] =
        React.useState<Dtos.ResultType | null>(null);

    const updateErrorResult = React.useCallback(
        (result: Dtos.ResultType) => {
            if (!errorResult || errorResult < result) {
                setErrorResult(result);
            }
        },
        [errorResult, setErrorResult]
    );

    const patientErrorState = React.useMemo(() => {
        return {
            result: errorResult,
            updateResult: updateErrorResult,
        };
    }, [errorResult, updateErrorResult]);

    const toggleRegistrationModal = React.useCallback(() => {
        setRegistrationModalOpen((state) => !state);
    }, [setRegistrationModalOpen]);

    const patientValid = errorResult === Dtos.ResultType.Valid;
    const patientError = errorResult === Dtos.ResultType.Error;
    const canRandomise =
        errorResult === Dtos.ResultType.Valid ||
        ((errorResult ?? Dtos.ResultType.Critical) <= Dtos.ResultType.Error &&
            isAdministrator)
            ? true
            : false;

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onRegisterClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Register</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you wish to register this patient?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onRegisterClose} color="primary">
                        No
                    </Button>
                    <Button
                        onClick={onRegisterOkClick}
                        color="primary"
                        autoFocus
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={ineligibleModalOpen}
                onClose={toggleIneligibleModal}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Mark Patient As Ineligible?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to mark patient{" "}
                        {patient?.studyNumber} as ineligible?
                        <br />
                        <br />
                        <strong>Reason</strong>
                    </DialogContentText>
                    <TextField
                        multiline={true}
                        fullWidth={true}
                        variant={"outlined"}
                        rows={10}
                        inputProps={{
                            onChange: (e) => {
                                setPatientStatusUpdateReason(
                                    e.currentTarget.value
                                );
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={toggleIneligibleModal} color="secondary">
                        No
                    </Button>
                    <Button
                        onClick={onIneligibleClick}
                        disabled={!patientStatusUpdateReason}
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>

            <PageLayout
                breadcrumbs={<PatientBreadcrumbs />}
                heading="Patient Summary"
            >
                <Paper className={classes.container}>
                    <PatientInformation patient={patient} />
                    <div className={classes.headingContainer}>
                        {(patient?.status === Dtos.PatientStatuses.Randomised ||
                            patient?.status ===
                                Dtos.PatientStatuses.Registered) && (
                            <Button
                                variant="contained"
                                color="primary"
                                className={classes.button}
                                component="a"
                                href={`/${patient?.id}/forms-due-schedule`}
                            >
                                Forms Due Schedule
                            </Button>
                        )}
                    </div>
                    <div className={classes.patientSummary}>
                        {!!patient && (
                            <PatientErrorContext.Provider
                                value={patientErrorState}
                            >
                                <PatientSummaryList patient={patient} />
                            </PatientErrorContext.Provider>
                        )}
                    </div>
                    <Dialog
                        open={registrationModalOpen}
                        onClose={toggleRegistrationModal}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">
                            Register Patient?
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                The patient has outstanding{" "}
                                {patientError
                                    ? "errors"
                                    : "ineligibility warnings"}{" "}
                                that should be corrected.
                                <br />
                                <br />
                                Register the patient?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={toggleRegistrationModal}
                                color="secondary"
                            >
                                No
                            </Button>
                            <Button
                                onClick={onRegisterOkClick}
                                color="primary"
                                variant="contained"
                                autoFocus
                            >
                                Yes
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <div className={classes.buttonSet}>
                        {patient?.status === Dtos.PatientStatuses.NewPatient &&
                            canUpdatePatient &&
                            !recruitmentDisabled && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onCompleteScreeningClick}
                                    className={classes.button}
                                    disabled={
                                        patientSaveState.state ===
                                            RequestState.Pending ||
                                        updating ||
                                        errorResult !== Dtos.ResultType.Valid
                                    }
                                >
                                    Complete Screening
                                </Button>
                            )}
                        {patient?.status === Dtos.PatientStatuses.Screened &&
                            canUpdatePatient &&
                            patientValid &&
                            !recruitmentDisabled && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onRegisterClick}
                                    className={classes.button}
                                    disabled={
                                        patientSaveState.state ===
                                            RequestState.Pending ||
                                        updating ||
                                        errorResult !== Dtos.ResultType.Valid
                                    }
                                >
                                    Register
                                </Button>
                            )}
                        {patient?.status === Dtos.PatientStatuses.Screened &&
                            canUpdatePatient &&
                            !patientValid &&
                            !recruitmentDisabled && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={toggleRegistrationModal}
                                    className={classes.button}
                                    disabled={
                                        patientSaveState.state ===
                                            RequestState.Pending ||
                                        updating ||
                                        !canRandomise
                                    }
                                >
                                    Register
                                </Button>
                            )}
                        {
                            //(patient?.status === Dtos.PatientStatuses.Registered || patient?.status === Dtos.PatientStatuses.Waivered) && canUpdatePatient && patientValid && !recruitmentDisabled && (
                            //    <Button
                            //        variant="contained"
                            //        color="primary"
                            //        className={classes.button}
                            //        onClick={onRandomiseClick}
                            //        disabled={patientSaveState.state === RequestState.Pending || updating || errorResult !== Dtos.ResultType.Valid}
                            //    >
                            //        Randomise
                            //    </Button>
                            //)
                        }
                        {
                            //(patient?.status === Dtos.PatientStatuses.Registered || patient?.status === Dtos.PatientStatuses.Waivered) && canUpdatePatient && !patientValid && !recruitmentDisabled && (
                            //    <Button
                            //        variant="contained"
                            //        color="primary"
                            //        className={classes.button}
                            //        onClick={toggleRegistrationModal}
                            //        disabled={patientSaveState.state === RequestState.Pending || updating || !canRandomise}
                            //    >
                            //        Randomise
                            //    </Button>
                            //)
                        }
                        {(patient?.status === Dtos.PatientStatuses.NewPatient ||
                            patient?.status ===
                                Dtos.PatientStatuses.Screened) &&
                            isAdministrator &&
                            !recruitmentDisabled && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={toggleIneligibleModal}
                                    className={classes.button}
                                    disabled={
                                        patientSaveState.state ===
                                        RequestState.Pending
                                    }
                                >
                                    Mark as Ineligible
                                </Button>
                            )}
                        {patient?.status === Dtos.PatientStatuses.Ineligible &&
                            isAdministrator &&
                            !recruitmentDisabled && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onRestoreClick}
                                    className={classes.button}
                                    disabled={
                                        patientSaveState.state ===
                                        RequestState.Pending
                                    }
                                >
                                    Restore Patient
                                </Button>
                            )}
                    </div>
                </Paper>
            </PageLayout>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default PatientSummary;
